<!-- Logistics Form - Vista inicial -->
<div *ngIf="showLogisticsForm">
  <header class="panel-header panel-header-compact">
    <h1>Exercise Logistics</h1>
    <p>Configure structural and pacing elements</p>
  </header>

  <div
    class="error-container"
    role="alert"
    aria-live="polite"
    *ngIf="generationError"
  >
    <div class="error-banner">
      <span class="error-icon">⚠</span>
      <div class="error-content">
        <p class="error-message">{{ generationError }}</p>
      </div>
      <button type="button" class="error-retry-btn" [disabled]="isGenerating">
        {{ isGenerating ? "Retrying..." : "Retry" }}
      </button>
    </div>
  </div>

  <div class="logistics-form">
    <section class="scope-section">
      <h2>Simulated event duration event</h2>

      <div class="chip-group">
        <button
          type="button"
          class="selection-chip"
          *ngFor="let duration of durationOptions"
          [class.selected]="isDurationSelected(duration.id)"
          (click)="selectDuration(duration.id)"
        >
          {{ duration.label }}
        </button>
      </div>
    </section>

    <section class="scope-section logistics-counter-section">
      <h2>Number of modules</h2>

      <div class="counter-grid">
        <article class="counter-card">
          <span class="counter-card-label">Number of modules</span>

          <div class="counter-control">
            <button
              type="button"
              class="counter-button"
              (click)="updateModuleCount(-1)"
              [disabled]="moduleCount === 0"
            >
              −
            </button>

            <span class="counter-value">{{
              formatCounterValue(moduleCount)
            }}</span>

            <button
              type="button"
              class="counter-button"
              (click)="updateModuleCount(1)"
            >
              +
            </button>
          </div>

          <span class="counter-caption">Crisis phase inject blocks</span>
        </article>

        <article class="counter-card">
          <span class="counter-card-label">Question per module</span>

          <div class="counter-control">
            <button
              type="button"
              class="counter-button"
              (click)="updateQuestionCount(-1)"
              [disabled]="questionCount === 0"
            >
              −
            </button>

            <span class="counter-value">{{
              formatCounterValue(questionCount)
            }}</span>

            <button
              type="button"
              class="counter-button"
              (click)="updateQuestionCount(1)"
            >
              +
            </button>
          </div>

          <span class="counter-caption">Discussion question each</span>
        </article>
      </div>
    </section>

    <section class="scope-section">
      <h2>Target Intensity</h2>

      <div class="intensity-grid">
        <button
          type="button"
          class="intensity-card"
          *ngFor="let intensity of intensityOptions"
          [class.selected]="isIntensitySelected(intensity.id)"
          (click)="selectIntensity(intensity.id)"
        >
          <strong>{{ intensity.title }}</strong>
          <span>{{ intensity.description }}</span>
        </button>
      </div>
    </section>

    <section class="scope-section logistics-artifacts-section">
      <h2>Exercise artifacts</h2>

      <div class="artifacts-stack">
        <label class="switch-row">
          <input type="checkbox" [(ngModel)]="includeNewsReports" />
          <span class="switch-pill">
            <span class="switch-knob"></span>
          </span>
          <span>Include simulated news reports</span>
        </label>

        <label class="switch-row">
          <input type="checkbox" [(ngModel)]="includeSocialFeedSimulation" />
          <span class="switch-pill">
            <span class="switch-knob"></span>
          </span>
          <span>Include social media feed simulation</span>
        </label>

        <div
          class="chip-group artifact-chip-group"
          *ngIf="includeSocialFeedSimulation"
        >
          <button
            type="button"
            class="selection-chip"
            *ngFor="let platform of socialPlatformOptions"
            [class.selected]="isSocialPlatformSelected(platform.id)"
            (click)="toggleSocialPlatform(platform.id)"
          >
            {{ platform.label }}
          </button>
        </div>

        <label class="switch-row">
          <input type="checkbox" [(ngModel)]="includeInternalEmails" />
          <span class="switch-pill">
            <span class="switch-knob"></span>
          </span>
          <span>Include simulated internal emails / memos</span>
        </label>
      </div>
    </section>

    <section class="scope-section">
      <h2>Word document template</h2>

      <p class="logistics-helper-copy">
        Upload a PDF or Word document (incident report, after-action review,
        situation report). Gemini will extract a scenario seed and generate a
        4th scenario option that stays close to your document.
      </p>

      <section class="document-section">
        <div
          class="document-dropzone"
          [class.drag-over]="templateDragOver"
          (dragover)="onTemplateDragOver($event)"
          (dragleave)="onTemplateDragLeave($event)"
          (drop)="onTemplateDrop($event)"
        >
          <div class="document-copy">
            <h2>Attach template</h2>
            <span>Or drag and drop your template here.</span>
          </div>

          <input
            #templateInput
            type="file"
            class="upload-input"
            accept=".doc,.docx,.pdf"
            (change)="onTemplateSelected($event)"
          />

          <button
            type="button"
            class="upload-button upload-button-step-two"
            (click)="templateInput.click()"
          >
            <img
              src="assets/icons/upload_icon.svg"
              alt="Upload"
              class="upload-button-icon"
            />
            <span>Select File</span>
          </button>
        </div>

        <div class="file-group" *ngIf="templateFiles.length > 0">
          <div class="file-list">
            <div class="file-card" *ngFor="let file of templateFiles">
              <div class="file-meta">
                <span class="file-icon">📄</span>
                <div class="file-copy">
                  <strong>{{ file.name }}</strong>
                  <span>{{ file.sizeLabel }}&nbsp;&nbsp;Completed</span>
                </div>
              </div>

              <button
                type="button"
                class="file-delete"
                aria-label="Remove template file"
                (click)="removeTemplateFile(file.id)"
              >
                <img src="assets/icons/trash_icon.svg" alt="Delete" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </section>

    <section class="scope-section">
      <h2>Exercise summary</h2>

      <div class="summary-grid">
        <article class="summary-card">
          <strong>{{ selectedDurationLabel | titlecase }}</strong>
          <span>Simulated Event Duration</span>
        </article>

        <article class="summary-card">
          <strong>{{ moduleCount }}</strong>
          <span>Number of Modules</span>
        </article>

        <article class="summary-card">
          <strong>{{ questionCount }}</strong>
          <span>Questions</span>
        </article>

        <article class="summary-card">
          <strong>{{ selectedIntensityTitle }}</strong>
          <span>Target Intensity</span>
        </article>
      </div>
    </section>
  </div>
</div>

<!-- Module Plan Review - Aparece después de "Proceed to generate" -->
<div *ngIf="!showLogisticsForm">
  <header class="panel-header panel-header-compact">
    <h1>Module Plan Review</h1>
    <p>
      Review the 3-modules structure, adjust timescales, and leave notes for
      Gemini before generating the exercise
    </p>
  </header>

  <div
    class="error-container"
    role="alert"
    aria-live="polite"
    *ngIf="generationError"
  >
    <div class="error-banner">
      <span class="error-icon">⚠</span>
      <div class="error-content">
        <p class="error-message">{{ generationError }}</p>
      </div>
      <button type="button" class="error-retry-btn" [disabled]="isGenerating">
        {{ isGenerating ? "Retrying..." : "Retry" }}
      </button>
    </div>
  </div>

  <div class="info-banner review-info-banner" *ngIf="!generationError">
    <span class="info-icon">i</span>
    <span>
      Gemini has proposed a module plan below based on your scenario and
      logistic. Each module show a 2-4 Sentences situations summary and a
      timescale (e.g T-30 days, T+0h). Click any timescale chip to edit it. Add
      a note in the comment box if you want to redirect a module's starting
      point in timeframe. Gemini will apply these when building your exercise.
    </span>
  </div>

  <div class="module-plan-toolbar" *ngIf="!generationError">
    <button type="button" class="module-plan-regenerate">
      Regenerate Module plan
    </button>

    <span class="module-plan-status">
      <span class="module-plan-status-icon">✓</span>
      <span>{{ modulePlans.length }} modules loaded</span>
    </span>
  </div>

  <section class="module-plan-stack" *ngIf="!generationError">
    <article
      class="module-plan-card"
      *ngFor="let modulePlan of modulePlans; let index = index"
    >
      <header class="module-plan-card-header">
        <h2>{{ modulePlan.title }}</h2>
        <span>{{ modulePlan.timescale }}</span>
      </header>

      <p class="module-plan-summary">
        {{ modulePlan.summary }}
      </p>

      <label class="field module-note-field">
        <span class="module-note-heading"
          >Designer Notes <span>[ Optional ]</span></span
        >
        <input
          type="text"
          [value]="modulePlan.notes"
          placeholder='e.g. "Move this module to T-2 months - the pandemic should just be starting here, not in the 40 countries yet"'
          (input)="updateModulePlanNote(modulePlan.id, $event)"
        />
      </label>
    </article>
  </section>

  <div
    class="info-banner review-info-banner review-info-banner-bottom"
    *ngIf="!generationError"
  >
    <span class="info-icon">i</span>
    <span>
      How this works in Phase 7: Once you proceed, Gemini will use these
      timescales and your notes as locked constraints when building the exercise
      document. The Mistral Red Team review will also be constrained to enhance
      within, not rewrite, this structure. Module count, timescales, and general
      direction will not change.
    </span>
  </div>
</div>
